import {
  ApplicationConfig,
  provideAppInitializer,
  inject,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  LOCALE_ID,
} from '@angular/core';
import { DATE_PIPE_DEFAULT_OPTIONS, DOCUMENT, registerLocaleData } from '@angular/common';
import localeBg from '@angular/common/locales/bg';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PrimeNG, providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { provideTransloco, TRANSLOCO_TRANSPILER, TranslocoService } from '@jsverse/transloco';
import { PluralTranspiler } from './core/plural-transpiler';
import { MessageService } from 'primeng/api';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { AuthService } from './core/services/auth.service';
import { LANG_STORAGE_KEY } from './core/services/entry-language';
import { TranslocoHttpLoader } from './core/transloco-http-loader';
import { firstValueFrom } from 'rxjs';
import { PRIMENG_TRANSLATIONS } from './core/primeng-translations';
import { clearIconPassThrough } from './core/primeng/clear-icon-pass-through';

const DEFAULT_LANGUAGE = 'bg';

registerLocaleData(localeBg);

export function preloadTranslation(transloco: TranslocoService) {
  return function () {
    const lang = transloco.getActiveLang() || DEFAULT_LANGUAGE;
    transloco.setActiveLang(lang);
    return firstValueFrom(transloco.load(lang));
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    MessageService,
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: 'dd.MM.yyyy' } },
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
    provideAnimations(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: { darkModeSelector: '.dark-mode' },
      },
    }),
    provideAppInitializer(() => inject(AuthService).init()),
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService);
      const saved = localStorage.getItem(LANG_STORAGE_KEY);
      if (saved === 'bg' || saved === 'en') {
        transloco.setActiveLang(saved);
      }
    }),
    provideTransloco({
      config: {
        availableLangs: ['bg', 'en'],
        defaultLang: DEFAULT_LANGUAGE,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(() => {
      // Календарът, бутоните „Да/Не“ и хиксът за изчистване на PrimeNG следват езика на интерфейса (#302, #303, #431),
      // а `<html lang>` — също: екранните четци и сричкопренасянето го четат (#352).
      const primeng = inject(PrimeNG);
      const document = inject(DOCUMENT);
      inject(TranslocoService).langChanges$.subscribe((lang) => {
        const translation = PRIMENG_TRANSLATIONS[lang] ?? PRIMENG_TRANSLATIONS['en'];
        primeng.setTranslation(translation);
        // Хиксът на `showClear` е бутон с име на езика на интерфейса (#431).
        primeng.pt.set(clearIconPassThrough(translation.clear ?? 'Clear'));
        document.documentElement.lang = lang;
      });
    }),
    provideAppInitializer(() => {
      const initializerFn = preloadTranslation(inject(TranslocoService));
      return initializerFn();
    }),
    { provide: TRANSLOCO_TRANSPILER, useClass: PluralTranspiler },
  ],
};
